import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trash2, Edit3, Star, Plus, ShieldCheck, ChevronRight, Users, Bell, Activity, Heart, Syringe, CalendarDays, FlaskConical, AlertOctagon, Watch } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import RefillPredictionsCard from "../components/RefillPredictionsCard";
import LanguageCard from "../components/LanguageCard";
import PillsIdCard from "../components/PillsIdCard";
import TodayDosesWidget from "../components/reminder/TodayDosesWidget";
import UpcomingThisWeek from "../components/dashboard/UpcomingThisWeek";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const EMPTY_ADDR = { label: "", line1: "", line2: "", city: "", state: "", pincode: "", phone: "", is_default: false };

const AddressForm = ({ initial, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(initial || EMPTY_ADDR);

    const onChange = (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [e.target.name]: v }));
    };

    const submit = (e) => {
        e.preventDefault();
        onSubmit(form);
    };

    return (
        <form
            onSubmit={submit}
            className="space-y-3 rounded-2xl border border-brand-100 bg-brand-50/30 p-5"
            data-testid="address-form"
        >
            <div className="grid grid-cols-2 gap-3">
                <input name="label" value={form.label} onChange={onChange} required placeholder="Label (Home, Office...)" className="input-clean col-span-2" data-testid="addr-label-input" />
                <input name="line1" value={form.line1} onChange={onChange} required placeholder="Line 1" className="input-clean col-span-2" data-testid="addr-line1-input" />
                <input name="line2" value={form.line2} onChange={onChange} placeholder="Line 2 (optional)" className="input-clean col-span-2" data-testid="addr-line2-input" />
                <input name="city" value={form.city} onChange={onChange} required placeholder="City" className="input-clean" data-testid="addr-city-input" />
                <input name="state" value={form.state} onChange={onChange} required placeholder="State" className="input-clean" data-testid="addr-state-input" />
                <input name="pincode" value={form.pincode} onChange={onChange} required placeholder="Pincode" className="input-clean" data-testid="addr-pincode-input" />
                <input name="phone" value={form.phone} onChange={onChange} required placeholder="Phone" className="input-clean" data-testid="addr-phone-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-600">
                <input type="checkbox" name="is_default" checked={!!form.is_default} onChange={onChange} data-testid="addr-default-checkbox" />
                Make this my default address
            </label>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50" data-testid="addr-cancel-btn">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="addr-save-btn">
                    {submitting ? "Saving..." : "Save address"}
                </button>
            </div>
        </form>
    );
};

const SecurityLinkCard = ({ twofaEnabled }) => (
    <Link
        to="/account/security"
        className="flex items-center justify-between gap-3 rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft transition-all hover:border-brand-200 hover:shadow-md dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40"
        data-testid="account-security-link"
    >
        <div className="flex items-center gap-3">
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${twofaEnabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"}`}>
                <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
                <p className="label-uppercase">Security</p>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{twofaEnabled ? "Two-factor enabled" : "Set up two-factor authentication"}</h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{twofaEnabled ? "Manage 2FA, backup codes and active sessions." : "Protect your account with a second factor + manage active devices."}</p>
            </div>
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-400" />
    </Link>
);

const CARE_TILES = [
    { to: "/family", testId: "account-link-family", icon: Users, label: "Family members", desc: "Manage profiles you care for", tint: "from-violet-500 to-indigo-500" },
    { to: "/caregivers", testId: "account-link-caregivers", icon: Bell, label: "Caregivers", desc: "Set escalation contacts", tint: "from-amber-500 to-rose-500" },
    { to: "/chronic-programs", testId: "account-link-chronic", icon: Heart, label: "Chronic programs", desc: "Diabetes, BP, thyroid…", tint: "from-rose-500 to-pink-500" },
    { to: "/health", testId: "account-link-health", icon: Activity, label: "Health tracker", desc: "Vitals, trends & PDF report", tint: "from-emerald-500 to-teal-500" },
    { to: "/vaccinations", testId: "account-link-vaccinations", icon: Syringe, label: "Vaccinations", desc: "Schedule & reminders", tint: "from-emerald-500 to-teal-500" },
    { to: "/appointments", testId: "account-link-appointments", icon: CalendarDays, label: "Appointments", desc: "All doctor visits", tint: "from-violet-500 to-indigo-500" },
    { to: "/lab-reminders", testId: "account-link-lab-reminders", icon: FlaskConical, label: "Lab reminders", desc: "Recurring tests", tint: "from-blue-500 to-cyan-500" },
    { to: "/emergency", testId: "account-link-emergency", icon: AlertOctagon, label: "Emergency profile", desc: "SOS, QR & contacts", tint: "from-rose-600 to-red-600" },
    { to: "/wearables", testId: "account-link-wearables", icon: Watch, label: "Wearables", desc: "Connect device (soon)", tint: "from-zinc-700 to-zinc-900" },
];

const CareHealthGrid = () => (
    <div data-testid="account-care-grid" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {CARE_TILES.map((t) => {
            const Icon = t.icon;
            return (
                <Link
                    key={t.to}
                    to={t.to}
                    data-testid={t.testId}
                    className="group flex items-start gap-3 rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft transition hover:border-brand-200 hover:shadow-md dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40"
                >
                    <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br ${t.tint} text-white`}>
                        <Icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t.label}</p>
                        <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{t.desc}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400 transition group-hover:translate-x-0.5 group-hover:text-brand-700" />
                </Link>
            );
        })}
    </div>
);

const AccountContent = () => {
    const { user, refresh } = useAuth();
    const [addrs, setAddrs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/addresses");
            setAddrs(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            const { data } = await api.post("/addresses", payload);
            setAddrs(data);
            setAdding(false);
            toast.success("Address added");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally {
            setSubmitting(false);
        }
    };

    const onUpdate = async (payload) => {
        try {
            setSubmitting(true);
            const { id, ...rest } = payload;
            const { data } = await api.patch(`/addresses/${id}`, rest);
            setAddrs(data);
            setEditing(null);
            toast.success("Address updated");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (id) => {
        try {
            const { data } = await api.delete(`/addresses/${id}`);
            setAddrs(data);
            toast.success("Address removed");
            refresh();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const onSetDefault = async (id) => {
        try {
            const { data } = await api.post(`/addresses/${id}/default`);
            setAddrs(data);
            toast.success("Default address updated");
            refresh();
        } catch (err) {
            toast.error("Could not set default");
        }
    };

    return (
        <div className="grid gap-8 lg:grid-cols-[320px_1fr]" data-testid="account-page">
            <aside className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft">
                <p className="label-uppercase">Signed in as</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900" data-testid="account-name">{user.full_name || "—"}</h2>
                <dl className="mt-6 space-y-3 text-sm">
                    <div>
                        <dt className="text-zinc-500">Email</dt>
                        <dd className="font-medium" data-testid="account-email">{user.email}</dd>
                    </div>
                    <div>
                        <dt className="text-zinc-500">Phone</dt>
                        <dd className="font-medium" data-testid="account-phone">{user.phone || "—"}</dd>
                    </div>
                    <div>
                        <dt className="text-zinc-500">Role</dt>
                        <dd>
                            <span className="inline-flex rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700" data-testid="account-role">{user.role}</span>
                        </dd>
                    </div>
                </dl>
            </aside>

            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <p className="label-uppercase">Addresses</p>
                        <h3 className="mt-1 text-xl font-semibold tracking-tight">Saved delivery addresses</h3>
                    </div>
                    {!adding && (
                        <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="add-address-btn">
                            <Plus className="h-4 w-4" /> Add
                        </button>
                    )}
                </div>

                {adding && (
                    <div className="mb-6">
                        <AddressForm
                            initial={EMPTY_ADDR}
                            onSubmit={onCreate}
                            onCancel={() => setAdding(false)}
                            submitting={submitting}
                        />
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">
                        <div className="h-28 skeleton rounded-2xl" />
                        <div className="h-28 skeleton rounded-2xl" />
                    </div>
                ) : addrs.length === 0 && !adding ? (
                    <EmptyState
                        title="No addresses yet"
                        description="Add a delivery address to speed up checkout (coming in Phase 3)."
                        action={<button onClick={() => setAdding(true)} className="btn-brand">Add address</button>}
                    />
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                        {addrs.map((a) => editing === a.id ? (
                            <div key={a.id} className="sm:col-span-2">
                                <AddressForm
                                    initial={a}
                                    onSubmit={(form) => onUpdate({ ...form, id: a.id })}
                                    onCancel={() => setEditing(null)}
                                    submitting={submitting}
                                />
                            </div>
                        ) : (
                            <div
                                key={a.id}
                                className="rounded-2xl border border-zinc-100 bg-white p-5 transition-all hover:border-brand-200"
                                data-testid={`address-card-${a.id}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-zinc-900">{a.label}</span>
                                            {a.is_default && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-700">
                                                    <Star className="h-3 w-3 fill-current" /> Default
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 text-sm text-zinc-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                                        <p className="text-sm text-zinc-600">{a.city}, {a.state} - {a.pincode}</p>
                                        <p className="text-sm text-zinc-500">{a.phone}</p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <button onClick={() => setEditing(a.id)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-brand-700" aria-label="Edit" data-testid={`addr-edit-${a.id}`}>
                                            <Edit3 className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => onDelete(a.id)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-red-500" aria-label="Delete" data-testid={`addr-delete-${a.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                {!a.is_default && (
                                    <button onClick={() => onSetDefault(a.id)} className="mt-4 text-xs font-medium text-brand-700 hover:underline" data-testid={`addr-set-default-${a.id}`}>
                                        Make default
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Account = () => {
    const { user } = useAuth();
    const twofaEnabled = !!user?.twofa?.enabled;
    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
                <header className="mb-8">
                    <p className="label-uppercase">Account</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Your profile</h1>
                </header>
                <AuthGuard>
                    <div className="space-y-6">
                        <PillsIdCard />
                        <SecurityLinkCard twofaEnabled={twofaEnabled} />
                        <CareHealthGrid />
                        <UpcomingThisWeek />
                        <TodayDosesWidget />
                        <RefillPredictionsCard />
                        <LanguageCard />
                        <AccountContent />
                    </div>
                </AuthGuard>
            </section>
        </Layout>
    );
};

export default Account;
